import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { UIOption } from './ui-option';
import type { ElementConfig } from '../ui-element.type';
import styles from './ui-option-plain.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIOption}
 * @alias UIOptionPlain
 * @element ui-option-plain
 * @classdesc Represents a class for <code>ui-option-plain</code> element.
 * An option that renders a symbolic icon (letter badge via <code>symbol</code> or
 * flag via <code>flag</code>) alongside a text label. Used with
 * <code>ui-dropdown layout="plain"</code> and <code>ui-option-list</code>.
 * @property {string} [symbol] {@attr symbol} Icon symbol.
 * @property {string} [flag] {@attr flag} Badge flag symbol for icon.
 * @slot
 * @example
 * <ui-dropdown layout="plain">
 *   <select>
 *     <option data-symbol="RR">Rand AB</option>
 *     <option data-symbol="D">"Rand", AB</option>
 *   </select>
 * </ui-dropdown>
 * @example
 * <ui-option-list label="Select company">
 *   <ui-option-plain optgroup text="Companies"></ui-option-plain>
 *   <ui-option-plain symbol="D" value="rand-ab" text='"Rand", AB'></ui-option-plain>
 *   <ui-option-plain symbol="CC" value="rand-as" text="AS Rand" selected></ui-option-plain>
 * </ui-option-list>
 */

@Component({
    tag: 'ui-option-plain',
    styles,
})
export class UIOptionPlain extends UIOption {
    static readonly HandlesOwnControl = true;

    @Attribute(String) symbol?: string;
    @Attribute(String) flag?: string;

    /**
     * @inheritDoc
     */
    render(): void {
        const label = this.getAttribute('text') || this.getAttribute('value');

        this.updateClassList({
            'ui-option-plain': true,
        });

        const items = (
            this.optgroup
                ? [this.buildOptGroupLabel()]
                : [
                      this.symbol
                          ? {
                                tagName: 'ui-icon-symbolic',
                                attributes: {
                                    class: 'ui-option-plain__icon',
                                    value: this.symbol,
                                    bgshape: 'square',
                                },
                            }
                          : undefined,
                      this.flag
                          ? {
                                tagName: 'ui-icon-flag',
                                attributes: {
                                    class: 'ui-option-plain__icon',
                                    code: this.flag,
                                },
                            }
                          : undefined,
                      label
                          ? {
                                tagName: 'span',
                                attributes: {
                                    class: 'ui-option-plain__label',
                                },
                                children: [label],
                            }
                          : undefined,
                      this.control
                          ? {
                                tagName: 'ui-icon',
                                attributes: {
                                    class: 'ui-option-plain__control-icon',
                                    glyph: 'redesign-chevron-down',
                                },
                            }
                          : undefined,
                  ].filter(Boolean)
        ) as Array<HTMLElement | DocumentFragment | ElementConfig>;

        this.insertElements(items);
        super.render();
    }
}
